import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useEscapeKey } from "../../../hooks/useEscapeKey";
import { applyOriginChanges, isApplicable, type ApplyResult, type OriginChange } from "../../../features/sg/origen";
import { Logos, fmt } from "./ui";

interface Props {
  changes: OriginChange[];
  isDarkMode: boolean;
  onClose: () => void;
  onApplied: () => void;
}

// Vista previa antes de escribir: SG tiene un solo Supabase (producción), así
// que cada cambio se ve y se elige antes de aplicarlo.
export default function OriginFixModal({ changes, isDarkMode, onClose, onApplied }: Props) {
  const { t } = useTranslation("sg");
  const applicable = changes.filter(isApplicable);
  const [selected, setSelected] = useState<Set<number>>(() => new Set(applicable.map((c) => c.id)));
  const [progress, setProgress] = useState<number | null>(null);
  const [result, setResult] = useState<ApplyResult | null>(null);
  const busy = progress != null && !result;
  useEscapeKey(onClose, !busy);

  const toggle = (id: number) =>
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  async function apply() {
    setProgress(0);
    try {
      const res = await applyOriginChanges(
        applicable.filter((c) => selected.has(c.id)),
        setProgress
      );
      setResult(res);
      if (res.ok.length) onApplied();
    } catch (e: any) {
      // Sin permiso de SG o sin red: no se escribió nada.
      setResult({ ok: [], failed: [{ id: 0, error: e.message }] });
    }
  }

  const muted = isDarkMode ? "text-gray-400" : "text-gray-500";
  const strong = isDarkMode ? "text-white" : "text-gray-900";
  const border = isDarkMode ? "border-dark-border" : "border-gray-200";
  const nameById = new Map(changes.map((c) => [c.id, c.name]));

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4" onMouseDown={(e) => e.target === e.currentTarget && !busy && onClose()}>
      <div className="absolute inset-0 bg-black/50" aria-hidden="true" />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="origin-fix-title"
        className={`relative w-full sm:max-w-2xl max-h-[90vh] flex flex-col rounded-t-2xl sm:rounded-2xl border shadow-xl ${isDarkMode ? "bg-dark-surface border-dark-border" : "bg-white border-gray-200"}`}
      >
        <header className={`p-5 border-b ${border}`}>
          <h2 id="origin-fix-title" className={`text-lg font-semibold ${strong}`}>
            {t("origen.title")}
          </h2>
          <p className={`mt-1 text-sm leading-relaxed ${muted}`}>{t("origen.description")}</p>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-2">
          {result ? (
            <div className="py-6 space-y-3 text-sm">
              <p className={strong}>{t("origen.applied", { n: fmt(result.ok.length) })}</p>
              {result.failed.length > 0 && (
                <ul className="space-y-1 text-red-500">
                  {result.failed.map((f) => (
                    <li key={f.id}>
                      {nameById.has(f.id) ? `${nameById.get(f.id)}: ` : ""}
                      {f.error}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ) : (
            <ul>
              {changes.map((c) => {
                const can = isApplicable(c);
                return (
                  <li key={c.id} className={`py-3 border-b last:border-b-0 flex items-start gap-3 ${border}`}>
                    <input
                      type="checkbox"
                      aria-label={c.name}
                      checked={can && selected.has(c.id)}
                      disabled={!can || busy}
                      onChange={() => toggle(c.id)}
                      className="mt-1 h-4 w-4 accent-primary-500 disabled:opacity-30"
                    />
                    <div className="min-w-0 flex-1">
                      <div className={`truncate text-sm font-medium ${strong}`}>{c.name}</div>
                      <div className="mt-1 flex items-center gap-2 text-xs">
                        <Logos list={c.before} empty={t("cuadre.platform.none")} />
                        <i className={`ri-arrow-right-line ${can ? "text-primary-500" : muted}`} aria-hidden="true" />
                        {can ? <Logos list={c.after} /> : <span className={muted}>{t("origen.noChange")}</span>}
                      </div>
                      {c.v5Manual && (
                        <div className="mt-1 text-xs text-amber-500">
                          {t(`origen.v5.${c.v5Manual}`)}{" "}
                          <Link to={`/sg/users/edit/${c.id}`} className="underline">
                            {t("cuadre.actions.editAccount")}
                          </Link>
                        </div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <footer className={`p-4 border-t flex flex-col sm:flex-row sm:items-center gap-3 ${border}`}>
          <span className={`flex-1 text-xs ${muted}`}>
            {result ? "" : busy ? t("origen.applying", { done: fmt(progress ?? 0), total: fmt(selected.size) }) : t("origen.warning", { n: fmt(selected.size) })}
          </span>
          <div className="flex gap-2 justify-end">
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className={`h-9 px-4 rounded-xl border text-sm font-medium transition-transform active:scale-[0.97] disabled:opacity-50 ${isDarkMode ? "border-dark-border text-gray-200 hover:bg-dark-card" : "border-gray-200 text-gray-700 hover:bg-gray-50"}`}
            >
              {result ? t("origen.close") : t("common.cancel")}
            </button>
            {!result && (
              <button
                type="button"
                onClick={apply}
                disabled={busy || selected.size === 0}
                className="h-9 px-4 rounded-xl bg-primary-500 hover:bg-primary-400 text-black text-sm font-semibold transition-[background-color,transform] active:scale-[0.97] disabled:opacity-50"
              >
                {busy ? <i className="ri-loader-4-line animate-spin" aria-hidden="true" /> : t("origen.apply", { n: fmt(selected.size) })}
              </button>
            )}
          </div>
        </footer>
      </div>
    </div>
  );
}
